import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Palette, Beer, Calendar, Heart } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { HERO_MURAL, RANGER_AVATAR } from "@/lib/constants";
import Seo from "@/components/Seo";
import { BusinessCard, EventCard, ArticleCard, OfferCard, TourCard, Overline } from "@/components/Cards";
import AdSlot from "@/components/AdSlot";
import { Button } from "@/components/ui/button";

const MARQUEE = ["MURALS", "•", "BREWERIES", "•", "GALLERIES", "•", "FIRST FRIDAYS", "•", "MAKERS", "•", "$5 WEDNESDAYS", "•", "STREET ART", "•"];

export default function Home() {
  const { user } = useAuth();
  const [featured, setFeatured] = useState([]);
  const [events, setEvents] = useState([]);
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState({});
  const [forYou, setForYou] = useState(null);

  useEffect(() => {
    api.get("/businesses", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 4)));
    api.get("/events", { params: { featured: true } }).then((r) => setEvents(r.data.slice(0, 3)));
    api.get("/articles", { params: { limit: 3 } }).then((r) => setArticles(r.data.slice(0, 3)));
    api.get("/stats").then((r) => setStats(r.data));
  }, []);

  useEffect(() => {
    if (user && user.role === "customer") api.get("/me/for-you").then((r) => setForYou(r.data)).catch(() => {});
    else setForYou(null);
  }, [user]);

  const hasForYou = forYou && (forYou.events?.length || forYou.deals?.length || forYou.tours?.length);

  return (
    <div>
      <Seo title="Denver's River North Art District" description="The official guide to Denver's RiNo Art District — discover murals, breweries, galleries, restaurants, events, and exclusive local offers." image={HERO_MURAL} />

      {/* Hero */}
      <section className="relative grain min-h-[88vh] flex items-end overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={HERO_MURAL} alt="RiNo street art mural in Denver" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-[#0A0A0A]/60" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pb-20 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <Overline>Denver, Colorado</Overline>
            <h1 className="font-display font-black text-5xl sm:text-7xl lg:text-8xl text-white mt-4 leading-[0.95] max-w-4xl">
              THIS IS <span className="text-[#FF0055] neon-text">RiNo</span>
            </h1>
            <p className="text-neutral-300 text-lg mt-6 max-w-xl leading-relaxed">
              River North Art District — Denver's boldest neighborhood of murals, breweries, galleries, and makers. Your official guide starts here.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/directory"><Button data-testid="hero-directory-btn" className="bg-[#FF0055] hover:bg-[#ff2d74] text-white rounded-none h-12 px-7 font-semibold text-base hover:shadow-[0_0_22px_rgba(255,0,85,0.6)] transition-shadow">Explore the Directory <ArrowRight size={18} /></Button></Link>
              <Link to="/events"><Button data-testid="hero-events-btn" variant="outline" className="rounded-none h-12 px-7 border-white/30 bg-transparent text-white hover:bg-white hover:text-black text-base">What's On</Button></Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <div className="bg-[#FF0055] py-3 overflow-hidden border-y border-[#FF0055]">
        <div className="marquee-track">
          {[...MARQUEE, ...MARQUEE].map((w, i) => (
            <span key={i} className="font-accent text-white text-2xl px-4">{w}</span>
          ))}
        </div>
      </div>

      {/* Stats */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10">
        <AdSlot placement="homepage_banner" />
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { n: stats.businesses, l: "Local Businesses", icon: Palette },
          { n: stats.events, l: "Upcoming Events", icon: Calendar },
          { n: stats.tours, l: "Tours & Experiences", icon: Beer },
          { n: stats.articles, l: "Ranger Guides", icon: Sparkles },
        ].map((s, i) => (
          <div key={i} className="border border-white/10 bg-[#141414] p-6" data-testid={`stat-${i}`}>
            <s.icon className="text-[#00E5FF] mb-3" size={22} />
            <div className="font-display font-extrabold text-4xl text-white">{s.n ?? "—"}</div>
            <div className="text-neutral-400 text-sm mt-1">{s.l}</div>
          </div>
        ))}
      </section>

      {/* For You — personalized by follows & subscriptions */}
      {hasForYou ? (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 border-b border-white/5" data-testid="for-you-section">
          <div className="flex items-end justify-between mb-8">
            <div>
              <Overline>Made for you</Overline>
              <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-white mt-2 flex items-center gap-2"><Heart size={26} className="text-[#FF0055]" /> For You{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</h2>
              <p className="text-neutral-400 text-sm mt-2">Based on the places and topics you follow.</p>
            </div>
            <Link to="/account" className="text-[#FF0055] text-sm font-semibold flex items-center gap-1 hover:gap-2 transition-all" data-testid="foryou-manage">Manage <ArrowRight size={15} /></Link>
          </div>
          {forYou.events?.length > 0 && (
            <div className="mb-8" data-testid="foryou-events">
              <h3 className="font-accent text-[#00E5FF] text-sm mb-4">Events you'll like</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{forYou.events.slice(0, 4).map((e, i) => <EventCard key={e.id} e={e} index={i} />)}</div>
            </div>
          )}
          {forYou.deals?.length > 0 && (
            <div className="mb-8" data-testid="foryou-deals">
              <h3 className="font-accent text-[#FFEA00] text-sm mb-4">Deals for you</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">{forYou.deals.slice(0, 4).map((o, i) => <OfferCard key={o.id} o={o} index={i} />)}</div>
            </div>
          )}
          {forYou.tours?.length > 0 && (
            <div data-testid="foryou-tours">
              <h3 className="font-accent text-[#00E5FF] text-sm mb-4">From operators you follow</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">{forYou.tours.slice(0, 3).map((t, i) => <TourCard key={t.slug} t={t} index={i} />)}</div>
            </div>
          )}
        </section>
      ) : (user && user.role === "customer" && forYou && !hasForYou) ? (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="for-you-empty">
          <div className="border border-white/10 bg-[#141414] p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3"><Heart size={22} className="text-[#FF0055]" /><p className="text-neutral-300 text-sm">Follow venues and pick your favorite topics to build your personalized "For You" feed.</p></div>
            <Link to="/account"><Button className="bg-[#FF0055] hover:bg-[#ff2d74] rounded-none">Set preferences</Button></Link>
          </div>
        </section>
      ) : null}

      {/* Featured directory */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <Overline>The Directory</Overline>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-white mt-2">Featured in RiNo</h2>
          </div>
          <Link to="/directory" className="text-[#FF0055] text-sm font-semibold flex items-center gap-1 hover:gap-2 transition-all" data-testid="see-all-directory">See all <ArrowRight size={15} /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {featured.map((b, i) => <BusinessCard key={b.id} b={b} index={i} />)}
        </div>
      </section>

      {/* Events */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <Overline>Happening Soon</Overline>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-white mt-2">Upcoming Events</h2>
          </div>
          <Link to="/events" className="text-[#FF0055] text-sm font-semibold flex items-center gap-1 hover:gap-2 transition-all" data-testid="see-all-events">Full calendar <ArrowRight size={15} /></Link>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {events.map((e, i) => <EventCard key={e.id} e={e} index={i} />)}
        </div>
      </section>

      {/* Ranger CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="relative grain border border-white/10 bg-[#141414] p-8 sm:p-12 flex flex-col md:flex-row items-center gap-8 overflow-hidden">
          <img src={RANGER_AVATAR} alt="The RiNo Ranger" className="relative z-10 w-32 h-32 object-cover border border-[#FF0055]" />
          <div className="relative z-10 flex-1 text-center md:text-left">
            <Overline>Meet Your Guide</Overline>
            <h2 className="font-display font-extrabold text-3xl text-white mt-2">The RiNo Ranger</h2>
            <p className="text-neutral-400 mt-3 max-w-2xl leading-relaxed">
              Our AI-powered local editor knows RiNo inside out — writing weekly things-to-do guides, insider listicles, and neighborhood news so you never miss a mural, a pour, or a First Friday.
            </p>
            <Link to="/guides"><Button className="mt-6 bg-[#00E5FF] text-black hover:bg-[#33ebff] rounded-none font-semibold" data-testid="ranger-guides-btn">Read the Guides <ArrowRight size={16} /></Button></Link>
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <Overline>From The Ranger</Overline>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-white mt-2">Latest Guides & Stories</h2>
          </div>
          <Link to="/guides" className="text-[#FF0055] text-sm font-semibold flex items-center gap-1 hover:gap-2 transition-all">All stories <ArrowRight size={15} /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {articles.map((a, i) => <ArticleCard key={a.id} a={a} index={i} />)}
        </div>
      </section>

      {/* Join CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="border border-[#FF0055] bg-gradient-to-br from-[#1a0410] to-[#141414] p-10 sm:p-16 text-center">
          <h2 className="font-display font-black text-3xl sm:text-5xl text-white">Own a business in RiNo?</h2>
          <p className="text-neutral-300 mt-4 max-w-2xl mx-auto text-lg">List your venue, post events and offers, join promotions like $5 Wednesdays, and get discovered by everyone visiting the district.</p>
          <Link to="/join"><Button className="mt-8 bg-[#FF0055] hover:bg-[#ff2d74] text-white rounded-none h-12 px-8 font-semibold text-base" data-testid="home-join-btn">Become a Member <ArrowRight size={18} /></Button></Link>
        </div>
      </section>
    </div>
  );
}
